'use client';

import { useEffect, useRef, useState } from 'react';
import { CatButton } from '@blocks-react/components/Button';
import styles from './notification-status-chips.module.scss';
import { NotificationChips } from '@cat-ecom/pcc-components';
import { useTranslations } from 'next-intl';
import { FilterChipSignal, NotificationStatusChipsProps } from './interfaces';

export const NotificationStatusChips = ({
  notificationFilter,
  setFilterChip
}: NotificationStatusChipsProps) => {
  const t = useTranslations();
  const [isShowMore, setIsShowMore] = useState<boolean>(false);
  const [isDisplayShowMore, setIsDisplayShowMore] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { orderStatus = [], account = { key: '', label: '' } } =
    notificationFilter;

  const handleRemovingOrderChip = (value: string): void =>
    setFilterChip(FilterChipSignal.OrderStatus, value);

  const handleRemoveAccountChip = (): void =>
    setFilterChip(FilterChipSignal.Account);

  const handleShowMore = (): void => {
    setIsShowMore(true);
  };

  const maxHeight = 70;

  useEffect(() => {
    const checkHeight = () => {
      if (containerRef?.current?.scrollHeight > maxHeight) {
        setIsDisplayShowMore(true);
      } else {
        setIsDisplayShowMore(false);
      }
    };

    const reqId = requestAnimationFrame(checkHeight);

    const chipContainerObserver = new ResizeObserver(checkHeight);
    const currentContainerRef = containerRef.current;
    if (currentContainerRef) {
      chipContainerObserver.observe(currentContainerRef);
    }

    return () => {
      cancelAnimationFrame(reqId);
      if (currentContainerRef) {
        chipContainerObserver.unobserve(currentContainerRef);
      }
    };
  }, [account, orderStatus]);

  return (
    <div className={`${styles['notification_chip_container']}`}>
      <div>
        <div
          className={
            !isShowMore
              ? styles['notification_chip_status']
              : styles['notification_chip_expand']
          }
          ref={containerRef}
        >
          {Object.keys(account)?.length > 0 && account.label !== '' && (
            <NotificationChips
              handleRemovingChip={handleRemoveAccountChip}
              notificationChip={account.label}
            />
          )}
          {orderStatus?.map((notificationChip) => (
            <NotificationChips
              key={notificationChip.orderStatus}
              handleRemovingChip={() =>
                handleRemovingOrderChip(notificationChip.orderStatus)
              }
              notificationChip={notificationChip.label}
            />
          ))}
        </div>
        <div className="d-flex justify-content-end">
          {!isShowMore && isDisplayShowMore && (
            <CatButton
              className={`${styles['show_more_btn']}`}
              variant="inline"
              onClick={handleShowMore}
              data-testid="SHOW_MORE"
            >
              {t('SHOW_MORE')}
            </CatButton>
          )}
        </div>
      </div>
    </div>
  );
};
